<script setup>
  import {computed, ref, watch} from "vue";

  import ComponentTextarea from "@components/Textarea/Textarea.vue";
  import {
    MedBadge,
    MedCheckbox,
    MedFormField,
    MedInfoButton,
    MedLabel,
    MedTooltip,
  } from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";
  import {usePrivacyGuard} from "@utils/Helpers/PrivacyGuard.js";

  defineOptions({
    name: "PagesRecordResultListClaimInteractionPdf",
  });

  const props = defineProps({
    claim: {
      type: Object,
      required: true,
    },
    communicationdoc: {
      type: Boolean,
      required: true,
    },
    communicationpatient: {
      type: Boolean,
      required: true,
    },
    hidden: {
      type: Boolean,
      required: true,
    },
    usernote: {
      type: [String, null],
      required: true,
    },
    usernotepatient: {
      type: [String, null],
      required: true,
    },
  });

  const emit = defineEmits([
    "update:communicationdoc",
    "update:communicationpatient",
    "update:hidden",
    "update:usernote",
    "update:usernotepatient",
    "open:confidentialCommentsDialog",
  ]);

  const {validatePrivacy} = usePrivacyGuard();

  const internalCommunicationdoc = ref(props.communicationdoc);
  const internalCommunicationpatient = ref(props.communicationpatient);
  const internalHidden = ref(props.hidden);
  const internalUsernote = ref(props.usernote);
  const internalUsernotepatient = ref(props.usernotepatient);

  watch(
    [
      internalHidden, // 0
      internalUsernote, // 1
      internalUsernotepatient, // 2
      internalCommunicationdoc, // 3
      internalCommunicationpatient, // 4
    ],
    (newValue, oldValue) => {
      for (let i = 0, l = newValue.length; i < l; i++) {
        if (newValue[i] !== oldValue[i]) {
          switch (i) {
            case 0:
              emit("update:hidden", newValue[i]);
              break;
            case 1:
              emit("update:usernote", newValue[i]);
              break;
            case 2:
              emit("update:usernotepatient", newValue[i]);
              break;
            case 3:
              emit("update:communicationdoc", newValue[i]);
              break;
            case 4:
              emit("update:communicationpatient", newValue[i]);
              break;
          }
        }
      }
    },
  );

  watch(
    () => props.communicationdoc,
    (newValue) => {
      internalCommunicationdoc.value = newValue;
    },
  );

  watch(
    () => props.communicationpatient,
    (newValue) => {
      internalCommunicationpatient.value = newValue;
    },
  );

  watch(
    () => props.hidden,
    (newValue) => {
      internalHidden.value = newValue;
    },
  );

  const hiddenTooltip = computed(() => {
    if (internalCommunicationdoc.value || internalCommunicationpatient.value) {
      return "Auswahl nicht möglich, da Ergebnis für einen Bericht ausgewählt ist";
    }
    return internalHidden.value
      ? "Ergebnis wurde als „nicht relevant“ markiert"
      : "Ergebnis wird als „nicht relevant“ markiert";
  });

  const privacyErrors = computed(() => {
    return {
      internalUsernote: validatePrivacy(internalUsernote, "Hinweis an Arzt"),
      internalUsernotepatient: validatePrivacy(internalUsernotepatient, "Hinweis an Patienten"),
    };
  });
</script>

<template>
  <div>
    <div class="flex items-center space-x-2 print:hidden">
      <h6 class="text-sm font-semibold">Kommunikation im PDF</h6>
      <MedInfoButton
        size="lg"
        side="top"
        aria-label="Kommunikation Info"
        :data-test="getTestId('interaction-pdf-info-button')"
      >
        <p class="max-w-sm text-sm">
          Wählen Sie Ergebnisse aus, die Sie im PDF-Dokument an den Arzt oder den Patienten kommunizieren möchten.
        </p>
      </MedInfoButton>
    </div>

    <ul class="divide-y divide-gray-100 *:py-2 print:divide-none">
      <li class="print:hidden in-[.print-archive]:print:block in-[.print-doc]:print:block">
        <div :class="cn('flex flex-wrap justify-between print:hidden', {'mb-2': internalCommunicationdoc})">
          <MedTooltip content="Ergebnis wird an Arzt übermittelt">
            <MedFormField orientation="horizontal">
              <MedCheckbox
                v-model="internalCommunicationdoc"
                :data-test="getTestId('interaction-internal-communication-doc-checkbox')"
                @click="internalHidden = false"
              />

              <MedLabel>an Arzt</MedLabel>
            </MedFormField>
          </MedTooltip>

          <MedTooltip
            v-if="$page.props.client.license.is_pro && claim.confidential_comment_count > 0"
            content="Rückmeldung des Arztes"
          >
            <MedBadge
              :label="claim?.confidential_comment_count"
              :data-test="getTestId('interaction-confidential-badge')"
            >
              <button
                class="cursor-pointer pr-2 text-sm text-mcred hover:underline"
                @click="$emit('open:confidentialCommentsDialog')"
              >
                Arztrückmeldung
              </button>
            </MedBadge>
          </MedTooltip>
        </div>

        <div :class="cn('print:block', {hidden: !internalCommunicationdoc})">
          <MedTooltip :class="cn('print:block')" content="Hinweise an Arzt werden in PDF-Dokument übernommen">
            <div class="mt-2 hidden pb-1 font-semibold print:hidden in-[.print-doc]:print:block">Hinweise:</div>
            <div class="mt-2 hidden pb-1 font-semibold print:hidden in-[.print-archive]:print:block">
              Hinweise an Arzt:
            </div>

            <ComponentTextarea
              v-model="internalUsernote"
              rows="2"
              placeholder="Hinweise an Arzt "
              class="my-0 pb-0 print:hidden"
              background="bg-gray-100 print:hidden"
              :privacy-guard="privacyErrors.internalUsernote"
              :test-id="getTestId('interaction-internal-user-note-doc')"
            />

            <div class="hidden min-h-20 border-b-2 border-b-gray-200 bg-gray-100 print:block">
              {{ internalUsernote }}
            </div>
          </MedTooltip>
        </div>
      </li>

      <li
        v-if="$page.props.client.license.is_pro && claim.feedback_url"
        class="hidden print:hidden in-[.print-doc.print-with-doctor-feedback]:print:block"
      >
        <a :href="claim.feedback_url" target="_blank" class="rounded bg-mcred p-1 font-semibold text-white">
          Rückmeldung geben
        </a>

        <div class="mt-2 text-xs leading-tight">zu den genannten Problemen und möglichen Lösungen</div>
      </li>

      <li class="print:hidden in-[.print-archive]:print:block in-[.print-patient]:print:block">
        <div
          :class="
            cn('print:hidden', {
              'mb-2': internalCommunicationpatient,
            })
          "
        >
          <MedTooltip content="Ergebnis wird an Patienten übermittelt" placement="left">
            <MedFormField orientation="horizontal">
              <MedCheckbox
                v-model="internalCommunicationpatient"
                :data-test="getTestId('interaction-internal-communication-patient-checkbox')"
                @click="internalHidden = false"
              />

              <MedLabel>an Patienten</MedLabel>
            </MedFormField>
          </MedTooltip>
        </div>

        <div :class="cn('print:block', {hidden: !internalCommunicationpatient})">
          <MedTooltip :class="cn('print:block')" content="Hinweise an Patienten werden in PDF-Dokument übernommen">
            <div class="mt-2 hidden pb-1 font-semibold print:hidden in-[.print-patient]:print:block">Hinweise:</div>
            <div class="mt-2 hidden pb-1 font-semibold print:hidden in-[.print-archive]:print:block">
              Hinweise an Patienten:
            </div>

            <ComponentTextarea
              v-model="internalUsernotepatient"
              rows="2"
              placeholder="Hinweise an Patienten"
              class="my-0 pb-0 print:hidden"
              background="bg-gray-100 print:hidden"
              :privacy-guard="privacyErrors.internalUsernotepatient"
              :test-id="getTestId('interaction-internal-user-note-patient')"
            />

            <div class="hidden min-h-20 border-b-2 border-b-gray-200 bg-gray-100 print:block">
              {{ internalUsernotepatient }}
            </div>
          </MedTooltip>
        </div>
      </li>

      <li class="print:hidden">
        <MedTooltip :content="hiddenTooltip" placement="left">
          <MedFormField orientation="horizontal" :disabled="internalCommunicationdoc || internalCommunicationpatient">
            <MedCheckbox v-model="internalHidden" :data-test="getTestId('interaction-internal-hidden-checkbox')" />

            <MedLabel>Ergebnis ausblenden</MedLabel>
          </MedFormField>
        </MedTooltip>
      </li>
    </ul>
  </div>
</template>
